enum OrderPlacement {
	PLACED_BY_USER,
	PLACED_BY_US,
	PLACED_BY_DOWNSTREAM,
	PLACED_BY_UNKNOWN,
	PLACED_BY_USER_COPY_TRADE_SOCIAL,
	PLACED_BY_USER_COPY_TRADE_AUTO,
	PLACED_BY_USER_COPY_TRADE_WATCHLIST,
}

type Currency = "USD" | "HKD";

export enum Instrument {
	STOCK = 1,
	OPTION = 2,
	FUTURE = 3,
	FOREX = 4,
	CRYPTO = 5,
	FX_CONVERSION = 6,
	CFD = 7,
	RF_MONEY_BOX = 8,
}

export enum OrderType {
	MARKET_ORDER = 0,
	LIMIT_ORDER = 1,
	STOP_ORDER = 2,
	STOP_LIMIT_ORDER = 3,
	TRAILING_STOP_AMOUNT_ORDER = 4,
	TRAILING_STOP_PERCENT_ORDER = 5,
}

export enum LimitType {
	OPEN_LONG = 0,
	OPEN_SHORT = 1,
	CLOSE_LONG = 2,
	CLOSE_SHORT = 3,
}

export enum HourType {
	TRADING_SESSION = 0,
	ALL_SESSIONS = 1,
}

export enum AccountType {
	CASH = 1, //  现金账户
	MARGIN = 2, // 保证金账户
}
export interface WatchlistSymbol {
	symbol: string;
	logoUrl: string;
	companyName: string;
	market: string;
	openPrice: number; // 股票加入股单时价格
	lastPrice: number; // 股票价格
	currency: number;
	createTime: number;
	dailyProfit: number; // 今日股单涨跌幅
	grossProfit: number; // 累计股单涨跌幅
	score: number;
	quantity: number; // negative if short position
}

export interface UserCurrencyAssets {
	accountId: string;
	userId: number;
	brokerAccountId: string;
	accountName: string;
	accountStatus: number;
	accountType: AccountType;
	currency: string;
	totalAssets: number;
	totalCashValue: number;
	grossPositionValue: number;
	dailyAssetChangePercent: number;
	dailyAssetChange: number;
	dailyPositionChangePercent: number;
	dailyPositionChange: number;
	accruedCash: number;
	accruedDividend: number;
	buyingPower: number;
	availableFunds: number; // 可用资金
	withdrawableCash: number;
	initialMargin: number;
	maintenanceMargin: number;
	riskRatio: number;
	marginDeficit: number;
	accruedTotal: number;
	frozenAmounts: Record<Currency, number>;
	availableCash: Record<Currency, number>; // 可用现金
	cashBuyingPowers: Record<Currency, number>; // 美元现金价值
	ledgers: Ledger[];
	positions: Position[];
}

export interface Ledger {
	userId: string;
	brokerAccountId: string;
	currency: string;
	cashBalance: number;
	withdrawableCash: number;
	exchangeRate: number;
	grossPositionValue: number;
	buyingPower: number;
	availableFunds: number;
	initialMargin: number;
	maintenanceMargin: number;
	loanableFunds: number;
}

export interface Position {
	userId: string;
	brokerAccountId: string;
	execId: string;
	source: string;
	symbol: string;
	quantity: number;
	openPrice: number;
	lastPrice: number;
	side: number;
	currency: string;
	open: boolean;
	createTime: string;
	profit: number;
	profitPercent: number;
	marketValue: number;
}

export interface TradingHour {
	market: string;
	marketName: string;
	today: TradingDaySchedule;
	next: TradingDaySchedule;
	previous: TradingDaySchedule;
}

export interface TradingDaySchedule {
	holiday?: string;
	is_open: boolean;
	start: number;
	end: number;
	lunch: boolean;
	lunch_start?: number;
	lunch_end?: number;
	pre_hours_start: number;
	pre_hours_end: number;
	post_hours_start: number;
	post_hours_end: number;
}

export interface FlowOrderPlanRequest {
	copyUserId: string;
	instrument?: Instrument;
	orderType: OrderType;
	limitType: LimitType;
	hourType: HourType;
	amount: number;
	endTime: number;
	taskType: TaskType;
	instruments?: Instrument[]; // auto stock use
	symbols?: CopySymbol[]; // copy stock use
	copyWatchlistId?: string; // copy stock use
	singleAmountMax?: number; // auto copy use
}

export enum TaskStatus {
	CANCELED = 0, // 已取消
	PENDING = 1, // 待执行
	IN_PROGRESS = 2, // 进行中(不可修改
	COMPLETED = 3, // 已完成
}
export interface UpdateFlowOrderPlanStatus {
	taskId: string;
	taskStatus?: TaskStatus;
	taskType: TaskType;
}

export interface CopySymbol {
	symbol: string;
	percentage: number;
}

export enum OpenCloseTypes {
	OPEN = 1, // 开仓
	CLOSE = 2, // 平仓
	OOEN_AND_CLOSE = 3, // 开仓&平仓
}

export enum OrderSide {
	BUY = 0, // 买入
	SELL = 1, // 卖出
}

export interface Watchlist {
	watchlistId: string;
	userId: string;
	nickname: string;
	avatar: string;
	watchlistName: string;
	createdBy: number;
	coverUrl: string;
	authorSays: string;
	allowFollow: boolean;
	followerCount: number;
	symbolCount: number;
	followers: Follower[];
	dailyProfit: number;
	grossProfit: number;
	followed?: boolean;
}

interface Follower {
	userId: number;
	avatar: string;
}

export enum TaskType {
	FOLLOW_ORDER_LIST = 1,
	AUTO_COPY = 2,
}

export enum SelectStatus {
	// -1 未变更不需选择 0未选择 1已选择
	UNCHANGED = -1,
	NOT_SELECTED = 0,
	SELECTED = 1,
}

export interface FollowOrderPlanDetail {
	id: number;
	taskId: number;
	userId: string;
	taskType: TaskType;
	copyUserId: number;
	copyWatchlistId: number;
	copyUserName: string;
	copyWatchlistName: string;
	copyUserAvatar?: string;
	instrument: Instrument;
	instruments: Instrument[];
	orderType: OrderType;
	limitType: LimitType;
	hourType: HourType;
	amount: number;
	taskStatus: TaskStatus;
	selectStatus: SelectStatus;
	createTime: string;
	updateTime: string;
	endTime: string;
	symbols: FollowOrderSymbol[];
	orders: FollowOrder[];
	singleAmountMax?: number;
}

export enum ChangeStatus {
	UNCHANGED = 0,
	CHANGED = 1, // 原股单变动
}

enum CompletedStatus {
	NOT_COMPLETED = 0,
	COMPLETED = 1,
}
interface FollowOrderSymbol {
	id: number;
	taskId: number;
	orderId: number;
	symbol: string;
	percentage: number;
	amount: number;
	changeStatus: ChangeStatus;
	completed: CompletedStatus;
	createTime: string;
	updateTime: string;
	logoUrl: string;
	symbolName: string;
}

export interface FollowOrder {
	userId: string;
	orderId: number;
	brokerAccountId: string;
	orderStatus: OrderStatus;
	source: string;
	exchange: string;
	symbol: string;
	instrument: number;
	orderType: OrderType;
	price: number;
	stop: number;
	quantity: number;
	filledPrice: number;
	filledQuantity: number;
	commissionCurrency: string;
	currency: string;
	side: number;
	session: number;
	validity: number;
	createTime: number;
	updateTime: number;
	orderSource: number;
	failureCause: number;
	description: string;
	exerciseState: number;
	logoUrl: string;
	companyName: string;
	amount: number;
	sessionStartTime: number;
	sessionEndTime: number;
	lastQuantity: number;
	lastPrice: number;
}

export enum OrderStatus {
	ORDER_SUBMITTED = 0, //订单已提交
	ORDER_PARTIALLY_FILLED = 1, // 部分成交
	ORDER_FILLED = 2, // 订单全部成交
	ORDER_CANCELLING = 3, // 订单取消中
	ORDER_PARTIALLY_CANCELLED = 4, // 订单部分取消
	ORDER_CANCELLED = 5, // 订单已取消
	ORDER_SUBMIT_FAILED = 6, // 订单提交失败
	ORDER_ERROR = 7, // 订单失败
	ORDER_EXPIRED = 8, // 订单失效
	ORDER_CANCEL_FAILED = 9, // 订单取消失败
	ORDER_UNDEFINED = 15, // 未知订单
}

export interface CheckCopyPlanCheckRequest {
	copyWatchlistId?: string; // copy stock use
	copyUserId?: string; // auto copy use
	taskType: TaskType;
}

export interface LeaderTradeRes {
	userId: string;
	orderCount: number; // 总订单数
	longOptionCount: number; // 小牛小熊订单数
	usStockCount: number; // 美股股票订单数
	startTime: number;
	endTime: number;
}

export interface SmartReq {
	userId?: string; // 用户Id
	leaderUserId?: string; // 单主Id
	instruments: Instrument[]; // 交易品种,
	amount?: string; // 跟单金额
}

export interface SmartRes {
	userId: string; // 用户Id
	leaderUserId: string; // 单主Id
	amount: number; // 跟单金额
	fractionalEnabled: boolean; // 开启碎股交易
	correctionEnabled: boolean; // 开启盘中修正
	priceDeviationProtectionEnabled: boolean; // 开启价差保护
	stockPriceDeviationThreshold: number; // 股票价差保护阈值
	optionPriceDeviationThreshold: number; // 期权价差保护阈值
	copyPositionEnabled: boolean; // 开启复制持仓
	availableFunds: number; // 最大可买
	positionConflict: boolean; // 相同持仓
}

export enum PlanStatus {
	UN_SUBMITTED = 0, // 待提交
	IN_PROGRESS = 1, // 进行中
	COMPLETED = 2, // 已结束
}

export interface CopyPositionPlanDetailRes {
	eventType: number;
	planId: number;
	userId: string;
	planStatus: PlanStatus;
	leaderUserId: string;
	amount: number;
	correctionEnabled: boolean;
	copyPositionEnabled: boolean;
	createTime: number;
	updateTime: number;
	instruments: {
		instrument: number;
		fractionalEnabled: boolean;
		maxPriceDeviation: number;
	}[];
	// profit: number;
	count: number;
	leaderType?: UserTypeEnum;
	totalProfit: number;
}

/**
 * code 枚举 - 用于跟单记录
 *  https://rockflow.feishu.cn/docx/JEE5duB4vo9Ux5x6sNdcpCRCngb
 *  https://rockflow.feishu.cn/sheets/W2ccsPVi3hsGZ8t7cIVcYe1SnSb
 */
export enum CopyOrderCodeEnum {
	code_0 = 0, // 跟单成功
	code_1001 = 1001, // 非交易时段跟单_盘后进入终态
	code_3010 = 3010, // 非交易时段跟单_盘后进入终态_没开盘中修正
	code_1002 = 1002, // 非交易时段跟单_收盘进入终态
	code_3009 = 3009, // 非交易时段跟单_收盘进入终态_没开盘中修正
	code_3002 = 3002, // 用户有反向待成交
	code_3013 = 3013, // 系统有反向待成交_盘前盘后_开仓
	code_3023 = 3023, // 系统有反向待成交_盘前盘后_平仓
	code_3014 = 3014, // 系统有反向待成交_盘中_开仓
	code_3024 = 3024, // 系统有反向待成交_盘中_平仓
	code_3015 = 3015, // 没有报价_开仓
	code_3025 = 3025, // 没有报价_平仓
	code_3035 = 3035, // 没有报价_平仓且牛人平完
	code_1009 = 1009, // 价差过大
	code_1004 = 1004, // 预估股数为0_股票盘前盘后_开启碎股
	code_3001 = 3001, // 预估股数为0_股票盘前盘后_没开盘中修正
	code_1005 = 1005, // 预估股数为0_股票盘前盘后_没开碎股
	code_1006 = 1006, // 预估股数为0_股票盘前盘后_股票不允许碎股交易
	code_1007 = 1007, // 预估股数为0_股票盘中
	code_1008 = 1008, // 预估股数为0_期权
	code_3007 = 3007, // 超过账户购买力_不可买
	code_2007 = 2007, // 超过账户购买力
	code_3008 = 3008, // 超过最大可买_不可买
	code_2008 = 2008, // 超过最大可买
	code_2002 = 2002, // 超过可用持仓
	code_1003 = 1003, // 不需要盘中修正
	code_4001 = 4001, // 订单提交失败
	code_9999 = 9999, // 其他未知错误
	code_1010 = 1010, // 用户已有持仓
	code_3003 = 3003, // 预估股数为0_预估交易金额为0
	code_3004 = 3004, // 预估股数为0_预估交易金额为负数
	code_2001 = 2001, // 超量平仓
	code_1011 = 1011, // 超量平仓且无法下单
}

export enum CopyChooseType {
	STOCK = 1, // 股票
	OPTION_STOCK = 2, // 期权
}

export enum CopyOrderRecordOrderSource {
	UNKNOWN = 0, // 未知
	COPY = 1, // 复制持仓
	AUTO = 2, // 自动跟单
	INTRADAY = 3, // 盘中调整
}

export interface CreateCopyReq {
	leaderUserId?: string;
	amount?: string;
	correctionEnabled?: boolean; // 开启盘中修正
	copyPositionEnabled?: boolean; // 开启复制持仓
	instruments?: {
		instrument: Instrument;
		fractionalEnabled?: boolean; // 开启碎股交易
		maxPriceDeviation?: number; // 价差保护阈值 -1 未开启 0 开启
	}[];
	recommend?: SmartRes;
}

export interface CopyPositionPlanRecordRes {
	id: string;
	availableFunds: number;
	correctionEnabled: boolean;
	depthPrice: number;
	eligibleToTrade: boolean;
	errorCode: number;
	fractionalEnabled: boolean;
	fractionalEnabledUserEstimatedOrderQuantity: number;
	fractionalEnabledUserOrderQuantity: number;
	fractionalLotSize: number;
	frozenAmount: number;
	hasUserPosition: boolean;
	instrument: number;
	leaderFilledPrice: number;
	leaderFilledQuantity: number;
	leaderNlv: number;
	leaderOrderId: number;
	leaderOrderQuantityRatio: number;
	leaderOrderSession: number;
	leaderPositionQuantity: number;
	leaderPositionValueRatio: number;
	leaderUserId: number;
	lotSize: number;
	marketSession: number;
	maxPriceDeviation: number;
	maxQuantityByCash: number;
	maxQuantityByPlan: number;
	openClose: number;
	orderSource: number;
	orderStatus: number;
	orderType: number;
	planAmount: number;
	planAvailableAmount: number;
	planBuyingPower: number;
	planFrozenAmount: number;
	planId: number;
	priceDeviation: number;
	remark: number;
	side: number;
	symbol: string;
	tradePrice: number;
	userActualPositionValue: number;
	userEstimatedOrderAmount: number;
	userEstimatedOrderQuantity: number; // 预估下单数
	userEstimatedOrderQuantityByAmount: number;
	userEstimatedOrderQuantityByQuantity: number;
	userId: number;
	userOrderPrice: number;
	userOrderQuantity: number; // 实际下单数
	userOrderSession: number;
	userTargetPositionValue: number;
	description: string;
	logoUrl: string;
	companyName: string;
	orderId: string;
	heavily: boolean;
	createTime: number;
	fullClosing: boolean;
	userEstimatedPositionValueRatio: number; // 预估持仓比例
	profit: number;
}

export enum UserTypeEnum {
	REAL_USER = 0, // 普通用户
	VIRTUAL_USER = 1, // 虚拟账户用户
	AI_COMBO = 2, // AI组合
	AI_STRATEGY = 3, // AI策略
}
export interface UserTypeInfoRes {
	id: number;
	userId: number;
	following: number;
	followers: number;
	copiers: number;
	copyTraders: number;
	copyTradingAmount: number;
	userType: UserTypeEnum;
	createTime: string;
	updateTime: string;
	strategy: UserInfo;
	creator: UserInfo;
	timeframe: string;
	monthlyBackTestProfit: number;
	yearlyBackTestProfit: number;
	language: string;
}

export interface UserInfo {
	userId: number;
	nickname: string;
	introduction: string;
	avatar: string;
	type: string;
}
